import { useState } from "react";
import { AuthUser } from "../../../../lib/auth/auth-store";
import { useBusinessVerification } from "../../../../composables/business-merchant/verification/useBusinessVerification";
import { Check, Clock, Info, TriangleAlert } from "lucide-react";
import SinglePageTitle from "../../../../components/ui/SinglePageTitle";
import SideSlidingModal from "../../../../components/modal/side-sliding-modal";
import CompanyInformationVerification from "./CompanyInformation";
import OwnerInformationVerification from "./OwnerInformation";
import DirectorInformationVerification from "./DirectorInformation";
import CacCertificateVerification from "./CacCertificate";
import StatusReportVerification from "./StatusReport";
import MemorandumVerification from "./Memorandum";
import AuthLayout from "../../../../components/layouts/auth";
import { useParams } from "react-router-dom";

const BusinessDocumentation = () => {
    const { code } = useParams<{ code: string }>();

    const User = AuthUser()

    const { data = [], isLoading: loadingVerification } = useBusinessVerification({
        code: code || '',
        enabled: !!code,
    });

    const [open, setOpen] = useState<string | null>(null)

    type VerificationStatus = 'verified' | 'pending' | 'reviewing' | 'flagged';

    interface VerificationItem {
        name: string;
        description: string;
        tag: string;
        status: VerificationStatus;
        element: any,
        display: boolean
    }

    const getStatus = (tag: string) => {
        const verification = data?.verifications?.find((item: any) => item.type === tag);
        return verification?.status || 'pending';
    }

    const getFlaggedReason = (tag: string) => {
        const verification = data?.verifications?.find((item: any) => item.type === tag);
        return verification?.flagged_reason || null;
    }

    const Verifications: VerificationItem[] = [
        {
            name: 'Company information',
            description: 'RC number, business type, description',
            tag: 'company_information',
            status: 'pending',
            element: <CompanyInformationVerification verified={getStatus('company_information') == 'verified'} open={open == 'company_information'} onClose={() => setOpen(null)} code={code || ''} />,
            display: true,
        },
        {
            name: "Owner's information",
            description: 'First name, last name, email, phone',
            tag: 'owner_information',
            status: 'pending',
            element: <OwnerInformationVerification verified={getStatus('owner_information') == 'verified'} open={open == 'owner_information'} onClose={() => setOpen(null)} code={code || ''} />,
            display: true,
        },
        {
            name: "Director's information",
            description: 'First name, last name, email, etc, minimum of 1 directors',
            tag: 'director_information',
            status: 'pending',
            element: <DirectorInformationVerification verified={getStatus('director_information') == 'verified'} open={open == 'director_information'} onClose={() => setOpen(null)} code={code || ''} />,
            display: data?.merchant?.business_information?.type_of_business == 'private_limited_company',
        },
        {
            name: "CAC Certificate",
            description: 'Upload your business registration certificate',
            tag: 'cac_certificate',
            status: 'pending',
            element: <CacCertificateVerification verified={getStatus('cac_certificate') == 'verified'} open={open == 'cac_certificate'} onClose={() => setOpen(null)} code={code || ''} />,
            display: true,
        },
        {
            name: 'Status report',
            description: 'Upload your company status report',
            tag: 'status_report',
            status: 'pending',
            element: <StatusReportVerification verified={getStatus('status_report') == 'verified'} open={open == 'status_report'} onClose={() => setOpen(null)} code={code || ''} />,
            display: true,
        },
        {
            name: 'Memorandum of Association (optional)',
            description: 'Upload memorandum of association',
            tag: 'memorandum_of_association',
            status: 'pending',
            element: <MemorandumVerification verified={getStatus('memorandum_of_association') == 'verified'} open={open == 'memorandum_of_association'} onClose={() => setOpen(null)} code={code || ''} />,
            display: data?.merchant?.business_information?.type_of_business == 'private_limited_company',
        },
        // {
        //     name: 'Other documents (optional)',
        //     description: 'Any additional supporting documents',
        //     tag: 'other_documents',
        //     status: 'pending',
        //     element: <OtherDocumentVerification/>,
        //     display: true,
        // }
    ];

    const getStatusConfig = (status: VerificationStatus) => {
        const configs = {
            verified: {
                bgGradient: 'from-green-50 to-emerald-50',
                borderColor: 'border-green-100/50',
                badgeBg: 'bg-green-100',
                badgeBorder: 'border-green-300',
                badgeText: 'text-green-700',
                icon: Check,
                iconBg: 'bg-green-600',
                label: 'Verified',
                svgColor: 'text-green-600',
            },
            reviewing: {
                bgGradient: 'from-blue-50 to-cyan-50',
                borderColor: 'border-blue-100/50',
                badgeBg: 'bg-blue-100',
                badgeBorder: 'border-blue-300',
                badgeText: 'text-blue-700',
                icon: Clock,
                iconBg: 'bg-blue-600',
                label: 'Under Review',
                svgColor: 'text-blue-600',
            },
            pending: {
                bgGradient: 'from-gray-50 to-slate-50',
                borderColor: 'border-gray-200/50',
                badgeBg: 'bg-gray-100',
                badgeBorder: 'border-gray-300',
                badgeText: 'text-gray-700',
                icon: Info,
                iconBg: 'bg-gray-400',
                label: 'Not Submitted',
                svgColor: 'text-gray-600',
            },
            flagged: {
                bgGradient: 'from-rose-50 to-red-50',
                borderColor: 'border-rose-100/50',
                badgeBg: 'bg-rose-100',
                badgeBorder: 'border-rose-300',
                badgeText: 'text-rose-700',
                icon: TriangleAlert,
                iconBg: 'bg-rose-600',
                label: 'Flagged Issue',
                svgColor: 'text-rose-600',
            },
        };
        return configs[status];
    };

    return (
        <AuthLayout>
            <div className="space-y-6">
                <div className="au flex justify-between items-center">
                    <div>
                        <SinglePageTitle link={`/business-merchants/${code}`} title="Business Documentation" />
                        <p className="text-gray-500 text-sm">View business documentation</p>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    {!loadingVerification && Verifications.map((item) => {
                        const config = getStatusConfig(getStatus(item.tag));
                        return (
                            item.display && <div key={item.tag} className={`relative au verify-card hover:border-primary overflow-hidden bg-linear-to-br ${config.bgGradient} rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border ${config.borderColor} p-6 group`}>
                                <div className="relative space-y-3">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <h3 className="font-bold text-lg text-gray-800">{item.name}</h3>
                                            <p className="themeTextMuted text-secondary text-sm mt-1">{item.description}</p>
                                        </div>
                                        <span className={`inline-block rounded-full ${config.badgeBg} border ${config.badgeBorder} px-3 py-1.5 flex gap-2 w-fit`}>
                                            <span className={`${config.badgeText} uppercase font-semibold text-xs`}>{config.label}</span>
                                        </span>
                                    </div>

                                    <div className="pt-3 ">
                                        {getFlaggedReason(item.tag) &&
                                            <div className="flex items-center text-xs gap-2 bg-red-50 text-red-500 p-2">
                                                {getFlaggedReason(item.tag)}
                                            </div>
                                        }
                                        {(getStatus(item.tag) === 'verified' || getStatus(item.tag) === 'reviewing') &&
                                            <SideSlidingModal
                                                buttonTrigger={<button className="mt-4 cursor-pointer w-full py-2 rounded-lg border border-border text-xs font-semibold text-secondary hover:bg-surface hover:border-primary hover:text-primary transition-all">
                                                    <span>
                                                        {getStatus(item.tag) === 'verified' ? 'View Details' : 'Review & Approve →'}
                                                    </span>
                                                </button>}
                                                slideTitle={item.name}
                                                open={open === item.tag}
                                                onClose={() => setOpen(null)}
                                                setState={(state) => state ? setOpen(item.tag) : setOpen(null)}
                                            >
                                                {item.element}
                                            </SideSlidingModal>
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </AuthLayout>
    )
}

export default BusinessDocumentation